/* Address autocomplete for the scan picker
   --------------------------------------------------------------------------
   Turns the street field into a search box that completes real addresses and
   fills city, state and ZIP from the one the customer picks.

   THIS FILE IS ADDITIVE AND OPTIONAL
   It never touches the booking logic. ta-mdi-slot-picker.js owns the hold, the
   line properties and whether Add to cart is enabled; this only writes into the
   four address inputs that file already reads. With no API key, or if Google
   fails to load, nothing here runs and the fields stay as plain text boxes that
   work exactly as before. That is the whole safety story: the flow degrades to
   what it is today rather than breaking.

   WHY EVENTS ARE DISPATCHED BY HAND
   Setting input.value in script does NOT fire an `input` event. The picker
   enables Add to cart from a listener on exactly that event, so an address
   filled by Google and never announced would leave the button dead with every
   field visibly populated - the worst kind of bug, because the page looks
   right. Every write here is followed by a dispatched event.

   WHY A CUSTOM LIST RATHER THAN GOOGLE'S WIDGET
   Google's PlaceAutocompleteElement renders its own input and would have to
   replace the street field, which is the one element the picker reads by
   data attribute. Fetching suggestions and drawing the list here leaves that
   input exactly where it is, and lets the dropdown match the theme.

   Google requires the "Powered by Google" mark whenever Places results are
   shown outside a Google map. It is rendered at the foot of the list and is
   not optional - see the attribution note in their terms.

   SESSION TOKENS
   Keystrokes are grouped into a session and the token is passed to the final
   details call. Google bills autocomplete per session rather than per keystroke
   when they are linked this way, so a customer typing twenty characters costs
   one lookup rather than twenty. A fresh token is minted after each selection.
*/
(function () {
  'use strict';

  var MIN_CHARS = 3;
  var DEBOUNCE_MS = 250;

  function loadMaps(key) {
    if (window.__taMapsPromise) return window.__taMapsPromise;
    window.__taMapsPromise = new Promise(function (resolve, reject) {
      /* Google's own bootstrap snippet, trimmed: it defines importLibrary and
         loads the core script once, regardless of how many callers ask. */
      var s = document.createElement('script');
      s.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(key) +
              '&libraries=places&loading=async&v=weekly';
      s.async = true;
      s.onerror = function () { reject(new Error('maps failed to load')); };

      /* The script's load event is NOT the signal that the API is ready.
         With loading=async Google fetches the rest of the library after this
         first file executes, so google.maps can still be undefined here -
         resolving on load alone made the very next line, importLibrary, throw
         a TypeError. That rejection was then swallowed by the catch below and
         the field quietly stayed a plain text box: working software, no error,
         no autocomplete. Wait for the function that is actually needed. */
      s.onload = function () {
        var tries = 0;
        (function ready() {
          if (window.google && window.google.maps &&
              typeof window.google.maps.importLibrary === 'function') {
            resolve(window.google);
          } else if (++tries > 100) {
            reject(new Error('google.maps.importLibrary never appeared'));
          } else {
            setTimeout(ready, 50);
          }
        })();
      };

      document.head.appendChild(s);
    });
    return window.__taMapsPromise;
  }

  function setup(root) {
    var key = root.dataset.placesKey;
    if (!key) return;

    var street = root.querySelector('[data-ta-street]');
    var city = root.querySelector('[data-ta-city]');
    var state = root.querySelector('[data-ta-state]');
    var zip = root.querySelector('[data-ta-zip]');
    if (!street || !city || !state || !zip) return;

    var country = (root.dataset.placesCountry || '').trim().toUpperCase();

    var box = document.createElement('div');
    box.className = 'tsp__ac';
    box.hidden = true;
    /* Placed as the street field's sibling so it can be positioned against it
       without the address grid having to become a positioning context. */
    street.parentNode.appendChild(box);

    var token = null;
    var timer = null;
    /* True only while this file is writing into the fields. Setting street.value
       fires an `input` event - which is the point, the picker listens for it -
       but this file listens for the same event to decide whether to search. So
       filling the field from a chosen address immediately searched for that
       address and reopened the list underneath it. The picker's listener must
       still run; only this one steps aside. */
    var filling = false;
    var items = [];
    var active = -1;
    var Places = null;

    function write(input, value) {
      filling = true;
      input.value = value;
      /* The picker's own listener lives on `input`; `change` is for anything
         else that may be watching, and costs nothing. */
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      filling = false;
    }

    var PIN = '<svg class="tsp__ac-pin" viewBox="0 0 24 24" aria-hidden="true">' +
      '<path d="M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11Z" fill="none" ' +
      'stroke="currentColor" stroke-width="1.6"/>' +
      '<circle cx="12" cy="10" r="2.6" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>';

    /* Shown the instant a third character lands, before the debounce has even
       fired. Without it the field sits silent for up to half a second on a slow
       connection and the customer types on, assuming nothing is happening. The
       rows are the same height as real ones, so nothing jumps when they are
       replaced. */
    function renderLoading() {
      box.innerHTML =
        '<ul class="tsp__ac-list" aria-hidden="true">' +
        '<li class="tsp__ac-item tsp__ac-item--ghost"><span class="tsp__ac-pinbox"></span>' +
        '<span class="tsp__ac-lines"><span class="tsp__ac-bar tsp__ac-bar--a"></span>' +
        '<span class="tsp__ac-bar tsp__ac-bar--b"></span></span></li>' +
        '<li class="tsp__ac-item tsp__ac-item--ghost"><span class="tsp__ac-pinbox"></span>' +
        '<span class="tsp__ac-lines"><span class="tsp__ac-bar tsp__ac-bar--c"></span>' +
        '<span class="tsp__ac-bar tsp__ac-bar--b"></span></span></li>' +
        '<li class="tsp__ac-item tsp__ac-item--ghost"><span class="tsp__ac-pinbox"></span>' +
        '<span class="tsp__ac-lines"><span class="tsp__ac-bar tsp__ac-bar--a"></span>' +
        '<span class="tsp__ac-bar tsp__ac-bar--d"></span></span></li></ul>' +
        '<p class="tsp__ac-by"><span class="tsp__ac-spin" aria-hidden="true"></span>Searching addresses</p>';
      items = [];
      active = -1;
      box.hidden = false;
    }

    /* A dead end needs saying. Closing the list silently looks identical to the
       search never having run, and the customer is left unsure whether to keep
       waiting. */
    function renderEmpty() {
      box.innerHTML =
        '<p class="tsp__ac-none">No matching address. Keep typing, or just enter it yourself.</p>';
      items = [];
      active = -1;
      box.hidden = false;
    }

    function close() {
      box.hidden = true;
      box.innerHTML = '';
      items = [];
      active = -1;
      street.setAttribute('aria-expanded', 'false');
    }

    function pick(suggestion) {
      var place = suggestion.toPlace();
      place.fetchFields({ fields: ['addressComponents'] }).then(function () {
        var part = {};
        (place.addressComponents || []).forEach(function (c) {
          (c.types || []).forEach(function (t) {
            part[t] = { long: c.longText, short: c.shortText };
          });
        });

        var line = [
          part.street_number && part.street_number.long,
          part.route && part.route.long
        ].filter(Boolean).join(' ');

        /* A place with no street number - someone picked a locality or a park -
           leaves whatever they typed rather than blanking the field on them. */
        if (line) write(street, line);

        if (part.locality) write(city, part.locality.long);
        else if (part.sublocality) write(city, part.sublocality.long);
        else if (part.postal_town) write(city, part.postal_town.long);

        if (part.administrative_area_level_1) write(state, part.administrative_area_level_1.short);
        if (part.postal_code) write(zip, part.postal_code.long);

        close();
        /* One session ends at the selection; the next keystroke starts another. */
        token = null;
      }).catch(close);
    }

    function render(suggestions) {
      box.innerHTML = '';
      items = [];
      if (!suggestions.length) { renderEmpty(); return; }

      var list = document.createElement('ul');
      list.className = 'tsp__ac-list';
      list.setAttribute('role', 'listbox');

      suggestions.forEach(function (s, i) {
        var p = s.placePrediction;
        if (!p) return;
        var li = document.createElement('li');
        li.className = 'tsp__ac-item';
        li.setAttribute('role', 'option');
        li.id = 'tsp-ac-' + i;
        li.innerHTML = PIN +
          '<span class="tsp__ac-lines"><span class="tsp__ac-main"></span>' +
          '<span class="tsp__ac-sub"></span></span>';
        li.querySelector('.tsp__ac-main').textContent = p.mainText ? p.mainText.text : p.text.text;
        li.querySelector('.tsp__ac-sub').textContent = p.secondaryText ? p.secondaryText.text : '';
        li.addEventListener('mousedown', function (e) { e.preventDefault(); pick(p); });
        list.appendChild(li);
        items.push({ el: li, prediction: p });
      });

      var mark = document.createElement('p');
      mark.className = 'tsp__ac-by';
      mark.textContent = 'powered by Google';
      box.appendChild(list);
      box.appendChild(mark);
      box.hidden = false;
      street.setAttribute('aria-expanded', 'true');
      active = -1;
    }

    function highlight(next) {
      if (!items.length) return;
      if (active >= 0) items[active].el.classList.remove('is-on');
      active = (next + items.length) % items.length;
      items[active].el.classList.add('is-on');
      street.setAttribute('aria-activedescendant', items[active].el.id);
    }

    function query(text) {
      if (!Places) return;
      if (!token) token = new Places.AutocompleteSessionToken();
      var req = {
        input: text,
        sessionToken: token,
        /* Addresses only. Unrestricted, Places answers a street-address field
           with whatever matches the string: typing "newyo" returned the city of
           New York, the STATE of New York, a hospital, and the New York-New
           York Hotel in Las Vegas. None of those is somewhere a van can be sent,
           and picking one leaves the street line without a number.

           These three are a house, a building, and a unit within one, which is
           every shape a home visit takes. Establishments and regions are
           excluded by their absence.

           Listed individually because the collection name `address` is NOT
           valid here - the API rejects it outright with "Invalid
           included_primary_types". `geocode` is accepted and returns the same
           results, but it also admits localities and postal codes, which is
           the problem this filter exists to solve. Verified against the live
           key: 'street_address' alone ranked an Irving, TX match above the
           Newport Beach one; all three together put Newport Beach first. */
        includedPrimaryTypes: ['street_address', 'premise', 'subpremise']
      };
      if (country) req.includedRegionCodes = [country];

      Places.AutocompleteSuggestion.fetchAutocompleteSuggestions(req)
        .then(function (res) { render(res.suggestions || []); })
        /* A failed lookup must never block typing: the field is still a text
           box and the customer can finish the address by hand. */
        .catch(close);
    }

    loadMaps(key).then(function (google) {
      return google.maps.importLibrary('places');
    }).then(function (lib) {
      Places = lib;
      street.setAttribute('role', 'combobox');
      street.setAttribute('aria-autocomplete', 'list');
      street.setAttribute('aria-expanded', 'false');
      /* The browser's own suggestions would sit on top of this list. */
      street.setAttribute('autocomplete', 'off');

      street.addEventListener('input', function () {
        if (filling) return;
        var text = street.value.trim();
        clearTimeout(timer);
        if (text.length < MIN_CHARS) { close(); return; }
        /* Immediately, not after the debounce: the skeleton is the answer to
           "is this doing anything", and it has to arrive before the doubt. */
        if (box.hidden || !items.length) renderLoading();
        timer = setTimeout(function () { query(text); }, DEBOUNCE_MS);
      });

      street.addEventListener('keydown', function (e) {
        if (box.hidden) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); highlight(active + 1); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); highlight(active - 1); }
        else if (e.key === 'Enter' && active >= 0) { e.preventDefault(); pick(items[active].prediction); }
        else if (e.key === 'Escape') { close(); }
      });

      street.addEventListener('blur', function () { setTimeout(close, 120); });
    }).catch(function () {
      /* No key, blocked referrer, offline, billing disabled - all land here and
         all mean the same thing: leave the plain fields alone. */
      close();
    });
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-ta-slot-picker][data-places-key]').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
